import { useQueries } from '@tanstack/react-query';

import { accessoryDetailQuery } from '@/services/accessories/queries';
import { type Accessory } from '@/services/accessories/types';
import { useAccessoryOrderStore } from '@/stores/accessoryOrderStore';

export type AccessoryOrderEntry = {
  slug: string;
  /** Name from the stored list; used while loading or if the part is gone. */
  name: string;
  quantity: number;
  accessory: Accessory | undefined;
  isPending: boolean;
  isGone: boolean;
};

/**
 * Re-reads each ordered accessory from the API so names, prices and availability
 * are current — a part can be withdrawn while it sits on the list.
 */
export function useAccessoryOrderItems(): { entries: AccessoryOrderEntry[]; isPending: boolean } {
  const items = useAccessoryOrderStore((s) => s.items);
  const results = useQueries({
    queries: items.map((item) => ({ ...accessoryDetailQuery(item.slug), retry: 0 })),
  });

  const entries: AccessoryOrderEntry[] = items.map((item, i) => {
    const r = results[i];
    const accessory = r?.data;
    return {
      slug: item.slug,
      name: accessory?.name ?? item.name,
      quantity: item.quantity,
      accessory,
      isPending: r?.isPending ?? false,
      isGone: r?.isError === true || (r?.isSuccess === true && accessory === undefined),
    };
  });

  const isPending = results.some((r) => r.isPending);
  return { entries, isPending };
}